import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { initFirebase } from '../firebase.js';

/**
 * Passwort vergessen und Passwort setzen.
 *
 * Die MAIL verschickt immer das Backend (`POST /api/auth/passwort-vergessen`), nie das
 * Client-SDK -- `sendPasswordResetEmail()` umginge unseren Absender (Architekturregel 6).
 * Eingeloest wird der Code dagegen hier mit dem Client-SDK (`confirmPasswordReset`): Das
 * verschickt nichts, und die Passwortrichtlinie (ENFORCE, scripts/setup-gcp.sh) setzt Identity
 * Platform dabei selbst durch. Laut Firebase-Doku gilt sie fuer Anlage, Aenderung und
 * Zuruecksetzen; live gemessen ist das fuer das Zuruecksetzen noch nicht.
 *
 * Nach dem Setzen meldet NICHTS automatisch an: Die Person meldet sich normal an, danach
 * schickt der Guard sie zur Einrichtung des zweiten Faktors (Architekturregel 4).
 */

export class PasswortFehler extends Error {
  constructor(readonly text: string) {
    super(text);
    this.name = 'PasswortFehler';
  }
}

export interface PasswortDienst {
  fordereAn(email: string): Promise<void>;
  /** Liefert die Adresse, zu der der Code gehoert. */
  pruefeCode(oobCode: string): Promise<string>;
  setze(oobCode: string, passwort: string): Promise<void>;
}

/** Spiegel der Richtlinie aus scripts/setup-gcp.sh, nur als Hilfe beim Tippen. */
export const PASSWORT_REGELN: ReadonlyArray<{ text: string; erfuellt: (pw: string) => boolean }> = [
  { text: 'mindestens 12 Zeichen', erfuellt: (pw) => pw.length >= 12 },
  { text: 'ein Großbuchstabe', erfuellt: (pw) => /\p{Lu}/u.test(pw) },
  { text: 'ein Kleinbuchstabe', erfuellt: (pw) => /\p{Ll}/u.test(pw) },
  { text: 'eine Ziffer', erfuellt: (pw) => /\d/.test(pw) },
  { text: 'ein Sonderzeichen', erfuellt: (pw) => /[^\p{L}\p{N}]/u.test(pw) },
];

export const MAX_PASSWORT = 100;

export function erfuelltRegeln(passwort: string): boolean {
  return passwort.length <= MAX_PASSWORT && PASSWORT_REGELN.every((r) => r.erfuellt(passwort));
}

const NETZWERK = 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung.';
const ABGELAUFEN =
  'Der Link ist abgelaufen oder wurde schon benutzt. Bitte fordern Sie einen neuen an.';

function codeVon(fehler: unknown): string {
  return typeof (fehler as { code?: unknown }).code === 'string'
    ? (fehler as { code: string }).code
    : '';
}

function linkMeldung(code: string): string {
  switch (code) {
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
    case 'auth/user-disabled':
    case 'auth/user-not-found':
      return ABGELAUFEN;
    case 'auth/network-request-failed':
      return NETZWERK;
    case 'auth/too-many-requests':
      return 'Zu viele Versuche. Bitte warten Sie einige Minuten.';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return 'Das Passwort erfüllt die Anforderungen nicht.';
    default:
      return 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.';
  }
}

export function firebasePasswortDienst(): PasswortDienst {
  return {
    async fordereAn(email) {
      let antwort: Response;
      try {
        antwort = await fetch('/api/auth/passwort-vergessen', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
      } catch {
        throw new PasswortFehler(NETZWERK);
      }
      if (antwort.ok) return;
      // Die Fehlertexte des Backends sind fuer Menschen geschrieben; fehlt einer (SPA-Fallback
      // statt Backend), bleibt es bei der allgemeinen Meldung.
      const daten = (await antwort.json().catch(() => null)) as {
        fehler?: { text?: unknown };
      } | null;
      const text = daten?.fehler?.text;
      throw new PasswortFehler(
        typeof text === 'string' && antwort.status < 500
          ? text
          : 'Die Anfrage konnte gerade nicht bearbeitet werden. Bitte versuchen Sie es später erneut.',
      );
    },

    async pruefeCode(oobCode) {
      const { auth } = await initFirebase();
      try {
        return await verifyPasswordResetCode(auth, oobCode);
      } catch (fehler) {
        throw new PasswortFehler(linkMeldung(codeVon(fehler)));
      }
    },

    async setze(oobCode, passwort) {
      const { auth } = await initFirebase();
      try {
        await confirmPasswordReset(auth, oobCode, passwort);
      } catch (fehler) {
        throw new PasswortFehler(linkMeldung(codeVon(fehler)));
      }
    },
  };
}
